import { getApiBaseUrl } from '../config/api';

// FEAT-046 — Web client for caregiver credential document upload + listing
// (MVP-18.20). Multipart upload goes to POST /api/credential-documents.
// Caregiver-owned list comes from GET /api/credential-documents/me.

export type CredentialDocType =
  | 'STATE_CERT'
  | 'TRAINING_CERT'
  | 'TRANSCRIPT'
  | 'OTHER';

export type CredentialDocStatus = 'PENDING_REVIEW' | 'ACCEPTED' | 'REJECTED';

export interface CredentialReviewDecision {
  id: string;
  adminId: string;
  decision: 'ACCEPTED' | 'REJECTED';
  reason: string | null;
  decidedAt: string;
}

export interface CredentialDocument {
  id: string;
  caregiverId: string;
  title: string;
  docType: CredentialDocType;
  notes: string | null;
  storageUri?: string;
  mimeType: string;
  sizeBytes: number;
  status: CredentialDocStatus;
  aiProcessingTag: Record<string, unknown> | null;
  uploadedAt: string;
  expiresAt: string | null;
  reviewDecisionId: string | null;
  reviewDecision?: CredentialReviewDecision | null;
}

export interface UploadCredentialDocumentInput {
  file: File;
  title: string;
  docType: CredentialDocType;
  notes?: string;
  expiresAt?: string;
}

// Allowed mime types + size limit. Kept in sync with
// `api/src/validation/credentialDocumentSchemas.ts` so client-side feedback
// matches the server's enforcement.
export const CREDENTIAL_DOC_MAX_BYTES = 25 * 1024 * 1024;
export const CREDENTIAL_DOC_ALLOWED_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
] as const;

export const CREDENTIAL_DOC_TYPE_OPTIONS: Array<{
  value: CredentialDocType;
  label: string;
  helper: string;
}> = [
  {
    value: 'STATE_CERT',
    label: 'State certification',
    helper: 'Like a state license or registration card.',
  },
  {
    value: 'TRAINING_CERT',
    label: 'Training certificate',
    helper: 'A completion certificate from a course you took.',
  },
  {
    value: 'TRANSCRIPT',
    label: 'Transcript',
    helper: 'A school or program transcript.',
  },
  {
    value: 'OTHER',
    label: 'Something else',
    helper: 'Any other supporting document.',
  },
];

class CredentialDocumentService {
  private async getAuthHeaders(includeJson = true): Promise<HeadersInit> {
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {};
    if (includeJson) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }

  validateFile(file: File): string | null {
    if (file.size > CREDENTIAL_DOC_MAX_BYTES) {
      return 'That file is larger than 25 MB. Could you try a smaller scan?';
    }
    const mime = file.type.toLowerCase();
    if (!CREDENTIAL_DOC_ALLOWED_MIME.includes(mime as never)) {
      return 'We can accept PDF, JPG, PNG, or HEIC files.';
    }
    return null;
  }

  async uploadDocument(
    input: UploadCredentialDocumentInput,
  ): Promise<{ document: CredentialDocument }> {
    const baseUrl = await getApiBaseUrl();
    const form = new FormData();
    form.append('file', input.file);
    form.append('title', input.title);
    form.append('docType', input.docType);
    if (input.notes) form.append('notes', input.notes);
    if (input.expiresAt) form.append('expiresAt', input.expiresAt);

    const response = await fetch(`${baseUrl}/credential-documents`, {
      method: 'POST',
      headers: await this.getAuthHeaders(false),
      body: form,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        body?.message ||
          body?.error ||
          'We could not upload that document just yet. Please try again.',
      );
    }

    return response.json();
  }

  async listMyDocuments(
    status?: CredentialDocStatus,
  ): Promise<{ documents: CredentialDocument[] }> {
    const baseUrl = await getApiBaseUrl();
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await fetch(`${baseUrl}/credential-documents/me${qs}`, {
      headers: await this.getAuthHeaders(),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        body?.message || body?.error || 'We could not load your documents right now.',
      );
    }
    return response.json();
  }
}

export const credentialDocumentService = new CredentialDocumentService();

// Status copy for caregiver-facing UI. Kept warm + encouraging per §1.2.
export const CAREGIVER_STATUS_COPY: Record<CredentialDocStatus, { label: string; helper: string }> = {
  PENDING_REVIEW: {
    label: 'Reviewing your document',
    helper: 'Our team is taking a careful look — usually a few days.',
  },
  ACCEPTED: {
    label: 'Document accepted',
    helper: "Thank you! We've added it to your profile.",
  },
  REJECTED: {
    label: 'Needs another look',
    helper: 'See the note below and feel free to upload an updated copy.',
  },
};
